import { AnalyticsClient } from './AnalyticsClient'
import { consentStore } from './consent/ConsentStore'
import { initVendorScripts, applyGrant } from './consent/consentMode'
import { ServerTransport } from './transport/ServerTransport'
import { Ga4ClientTransport } from './transport/Ga4ClientTransport'
import { MetaPixelTransport } from './transport/MetaPixelTransport'
import { initDebugCounters } from './util/debugCounters'
import { captureFbclidFromUrl, ensureFbp } from './util/fbCookies'
import { getAnonymousExternalId } from './util/anonymousId'

// Set REACT_APP_ANALYTICS_ENABLED=false to silence all client-side analytics
// (no GTM/fbq init, no /api/analytics/events requests, no dataLayer pushes).
// Defaults to enabled. Use in local .env.development.local for quiet dev runs.
const ANALYTICS_ENABLED = process.env.REACT_APP_ANALYTICS_ENABLED !== 'false'

if (ANALYTICS_ENABLED) {
  initDebugCounters()
  initVendorScripts()
  // Run before any event ships so transports always see a populated _fbc when
  // the visitor landed via a Meta ad click, and a stable _fbp even when the
  // Meta Pixel script is blocked (ad-blocker, denied consent).
  captureFbclidFromUrl()
  ensureFbp()
  // Mint the anonymous external_id eagerly so the very first event of the
  // session carries it (otherwise lazy creation would still work but the first
  // page_view race could ship without one if an unrelated localStorage failure
  // throws synchronously).
  getAnonymousExternalId()
  if (consentStore.isGranted()) {
    applyGrant()
  }
}

export const analytics = new AnalyticsClient(
  ANALYTICS_ENABLED
    ? [
        new ServerTransport(),
        new Ga4ClientTransport(consentStore),
        new MetaPixelTransport(consentStore),
      ]
    : [],
  consentStore,
)

export { consentStore } from './consent/ConsentStore'
export type { DomainEvent, EventPayload, EventItem, ConsentSnapshot } from './types'
export { productToEventItem, cartItemToEventItem } from './events/mappers'
export { ConsentProvider, useConsent } from './react/ConsentProvider'
export { buildPurchaseAnalyticsContext, consumePurchaseEventId } from './purchaseContext'
export type { AnalyticsContextPayload } from './purchaseContext'
export { setAnalyticsIdentity, clearAnalyticsIdentity } from './identity'
export type { AnalyticsIdentity } from './identity'

export function trackEvent(
  ...args: Parameters<typeof analytics.track>
): void {
  analytics.track(...args)
}
